import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { ListResponse } from "types/api";
import { Disagreement } from "types/disagreements";


interface DetectorObj {
  predictor__label_prompt: string;
  predictor__name: string;
  predictor__id: string;
  num_disagreements: number;
}

interface DetectorsCountResponse {
  detectors: DetectorObj[];
}

export interface SelectDisagreementProps {
  currentDisagreement: Disagreement | null;
  setCurrentDisagreement: React.Dispatch<React.SetStateAction<Disagreement | null>>;
  shouldRefetch: boolean;
  setShouldRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  showCloseDisagreements?: boolean;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

export function SelectDisagreement(props: SelectDisagreementProps) {
  const {
    currentDisagreement,
    setCurrentDisagreement,
    shouldRefetch,
    setShouldRefetch,
    showCloseDisagreements = false,
    setError,
  } = props;

  const [detector, setDetector] = useState<DetectorObj | null>(null);
  const [textValue, setTextValue] = useState<string>("");

  // Menu detectors
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Menu disagreements
  const [anchorElDis, setAnchorElDis] = useState<null | HTMLElement>(null);
  const openDisagreements = Boolean(anchorElDis);

  // Params
  const [searchParams, setSearchParams] = useSearchParams();
  const disagreementId = searchParams.get("disagreement_id");
  const detectorId = searchParams.get("detector_id");

  // fetch Detectors
  const detectorsUrl = `${JANZU_URL}reef-api/disagreements/count`;
  const {
    get: getDetectorsData,
    data: detectorsData,
    loading: loadingDetectors,
  } = useFetchCustom<DetectorsCountResponse>(detectorsUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  // fetch Disagreements
  const disagreementsUrl = `${JANZU_URL}reef-api/disagreements`;
  const {
    data: disagreementsData,
    get: getDisagreements,
    loading: loadingDisagreements,
  } = useFetchCustom<ListResponse<Disagreement>>(disagreementsUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  useEffect(() => {
    if (currentDisagreement?.id) {
      return;
    }

    getDetectorsData(`${showCloseDisagreements ? "?state=OPEN" : ""}`);
  }, [getDetectorsData, shouldRefetch, showCloseDisagreements, currentDisagreement]);

  useEffect(() => {
    if (detector?.predictor__id) {
      getDisagreements(`?detector_id=${detector?.predictor__id}${showCloseDisagreements ? "&state=OPEN" : ""}`);
    }
  }, [detector, getDisagreements, shouldRefetch, showCloseDisagreements]);

  useEffect(() => {
    setShouldRefetch((prev) => !prev);

    const refetchInterval = setInterval(() => {
      setShouldRefetch((prev) => !prev);
    }, 5000);

    return () => {
      clearTimeout(refetchInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (detectorsData?.detectors && detectorsData?.detectors?.length > 0 && detectorId) {
      const findDetector = detectorsData?.detectors.find((i) => i.predictor__id === detectorId);
      if (findDetector) {
        setDetector(findDetector);
        if (findDetector?.num_disagreements === 0) {
          setError("This detector has no disagreements.");
        }
      } else {
        setError("This detector has no disagreements.");
      }
    }

    if (detectorsData?.detectors && detectorsData?.detectors?.length === 0) {
      setError("There are no detectors with disagreements");
    }
  }, [detectorsData, detectorId, setDetector, setError]);

  useEffect(() => {
    if (disagreementsData?.results && disagreementsData?.results?.length > 0) {
      if (disagreementId) {
        // eslint-disable-next-line
        const findDisagreement = disagreementsData?.results.find((i) => i.id == disagreementId);
        if (findDisagreement) {
          setCurrentDisagreement(findDisagreement || disagreementsData?.results[0]);
          return;
        }
      }
      setCurrentDisagreement(disagreementsData?.results[0]);
    }
  }, [disagreementsData, disagreementId, setCurrentDisagreement]);

  const dataFiltered = useMemo(() => {
    const dataList = detectorsData?.detectors || [];
    if (textValue.length > 0 && dataList) {
      return dataList?.filter((row) => {
        const searchStr = row.predictor__name + row.predictor__name;
        return searchStr.toLowerCase().includes(textValue.toLowerCase());
      });
    }

    return dataList;
  }, [textValue, detectorsData]);

  const handleClickOpen = (event: React.MouseEvent<HTMLElement>, menu: "disagreements" | "detectors") => {
    if (menu === "disagreements") {
      setAnchorElDis(event.currentTarget);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDetectors = () => {
    setAnchorEl(null);
  };

  const handleCloseDisagreements = () => {
    setAnchorElDis(null);
  };

  const handleClickSelectDetector = (data: DetectorObj) => {
    setSearchParams((params) => {
      params.set("detector_id", data.predictor__id);
      params.set("disagreement_id", "");
      return params;
    });
    handleCloseDetectors();
  };

  const handleClickSelectDisagreement = (disagreement: Disagreement) => {
    setSearchParams((params) => {
      params.set("disagreement_id", disagreement.id);
      return params;
    });
    handleCloseDisagreements();
  };

  const existText = textValue.trim().length > 0;

  const disagreementList =
    disagreementsData?.results && disagreementsData?.results?.length > 0 ? disagreementsData?.results : [];

  return (
    <Box className="flex items-center gap-2 ">
      {detector && <Typography variant="subtitle" textTransform="uppercase">Detector:</Typography>}
      <Button
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={(e) => handleClickOpen(e, "detectors")}
        endIcon={<KeyboardArrowDownIcon width={20} height={20} />}
        className="text-white gap-2"
      >
        {loadingDetectors && (
          <CircularProgress color="inherit" sx={{ width: "25px!important", height: "25px!important" }} />
        )}
        <Typography variant="subtitle" fontWeight="bold" textTransform="uppercase">
          {detector?.predictor__name || "Choose Detector"}
        </Typography>
      </Button>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseDetectors}
        PaperProps={{
          style: {
            padding: "10px",
            width: "600px",
          },
        }}
      >
        <TextField
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          size="small"
          fullWidth
          placeholder="Search by Name/ID..."
          className="mb-2"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box className="flex gap-2">
                  {existText && (
                    <IconButton className="p-1" onClick={() => setTextValue("")}>
                      <CloseIcon sx={{ opacity: "50%" }} />
                    </IconButton>
                  )}
                  <IconButton className="p-1">
                    <SearchIcon sx={{ transform: "rotate(90deg)", opacity: "50%" }} />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
        />
        {loadingDetectors ? (
          <Box>
            <Skeleton width="100%" height="36px" />
            <Skeleton width="100%" height="36px" />
            <Skeleton width="100%" height="36px" />
          </Box>
        ) : (
          <Box className="max-h-[180px] overflow-y-auto">
            {dataFiltered && dataFiltered?.length > 0 ? (
              dataFiltered.map((option) => (
                <MenuItem
                  key={option.predictor__id}
                  selected={option.predictor__id === detector?.predictor__id}
                  onClick={() => handleClickSelectDetector(option)}
                >
                  <Box className="flex gap-2">
                    <b>{`(${option.num_disagreements}) ${option.predictor__name}`}</b>
                    <Box>
                      <b>-</b>
                    </Box>
                    <Box>{option.predictor__id}</Box>
                  </Box>
                </MenuItem>
              ))
            ) : (
                  <Typography fontWeight="bold" align="center">
                    No detectors
                  </Typography>
            )}
          </Box>
        )}
      </Menu>
      {detector && (
        <Box className="flex gap-2 items-center">
          <ArrowForwardIcon />
          {currentDisagreement && (
            <Typography variant="subtitle" textTransform="uppercase">
              Disagreement:
            </Typography>
          )}
          <Button
            aria-label="more"
            id="long-button"
            aria-controls={openDisagreements ? "long-menu" : undefined}
            aria-expanded={openDisagreements ? "true" : undefined}
            aria-haspopup="true"
            onClick={(e) => handleClickOpen(e, "disagreements")}
            endIcon={<KeyboardArrowDownIcon width={20} height={20} />}
            className="text-white"
          >
            <Typography variant="subtitle" fontWeight="bold" textTransform="uppercase">
              {currentDisagreement?.id || "Choose Disagreement"}
            </Typography>
          </Button>
        </Box>
      )}
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorElDis}
        open={openDisagreements}
        onClose={handleCloseDisagreements}
        PaperProps={{
          style: {
            padding: "10px",
            width: "auto",
          },
        }}
      >
        {loadingDisagreements && disagreementList.length === 0 ? (
          <Box>
            <Skeleton width="400px" height="36px" />
            <Skeleton width="400px" height="36px" />
            <Skeleton width="400px" height="36px" />
          </Box>
        ) : (
          <Box className="max-h-[180px] overflow-y-auto">
            {disagreementList && disagreementList?.length > 0 ? (
              disagreementList.map((option) => (
                <MenuItem
                  key={option.id}
                  selected={option.id === currentDisagreement?.id}
                  onClick={() => handleClickSelectDisagreement(option)}
                >
                  <Box className="flex gap-2">
                    <b>{option.id}</b>
                    <Box>
                      <b>-</b>
                    </Box>
                    <Box>{option.state}</Box>
                  </Box>
                </MenuItem>
              ))
            ) : (
                  <Typography fontWeight="bold" align="center">
                    No disagreements
                  </Typography>
            )}
          </Box>
        )}
      </Menu>
    </Box>
  );
}
